import axios, { InternalAxiosRequestConfig } from 'axios';
import { authPath } from '../utils/shopSlug';
import { isPublicAuthPath, isSsoRedemptionRequest } from '../utils/publicAuthPath';
import { rejectStatusZero } from './statusZero';
import { serializeSessionRequest, sessionGeneration, sessionIsCurrent } from './authSession';

let accessToken: string | null = null;
// GROC-02: the exact persisted EX-01 tenant/actor scope + native session
// locator the backend mints at every login/register/refresh (`app.services.
// native_session_authority`). Opaque here — never interpreted or rebuilt
// client-side, only stored and echoed back verbatim on a native-scoped
// vendor request (see groceryPick.ts, the first web feature that needs it;
// the mobile apps have used the identical object for longer).
let nativeSessionScope: Record<string, unknown> | null = null;
let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: unknown) => void }[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((p) => {
    if (error) {
      p.reject(error);
    } else {
      p.resolve(token!);
    }
  });
  failedQueue = [];
};

export const setAccessToken = (token: string | null) => {
  accessToken = token;
  // Notify long-lived consumers (e.g. the vendor WebSocket hook) so
  // they can reconnect with the fresh token instead of using the old
  // one until natural expiry.
  try {
    window.dispatchEvent(
      new CustomEvent('vendoriq:token-rotated', { detail: { token } })
    );
  } catch {
    // ignore — non-browser env
  }
};

export const getAccessToken = () => accessToken;

/** Structural check only (an object with the three keys `current_owner`/
 * `scope_for` require) — never a claim that its contents are current;
 * the server re-verifies that on every write. `null` clears it (logout,
 * a refresh whose response carried none — a pre-NAT session). */
export const setNativeSessionScope = (scope: Record<string, unknown> | null) => {
  nativeSessionScope = scope && typeof scope === 'object' && !Array.isArray(scope) &&
    typeof scope.tenant_context === 'object' && typeof scope.actor_context === 'object' &&
    typeof scope.server_session_ref === 'string' && scope.server_session_ref.length > 0 ? scope : null;
  try {
    window.dispatchEvent(new CustomEvent('vendoriq:token-rotated', { detail: { token: accessToken } }));
  } catch {
    // ignore — non-browser env
  }
};

export const getNativeSessionScope = () => nativeSessionScope;

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Read the vendoriq_csrf cookie (non-httpOnly so the SPA can see it).
// Backend sets it via SecurityHeadersMiddleware.
function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|; )vendoriq_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

const STATE_CHANGING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const scoped = config as InternalAxiosRequestConfig & { _authGeneration?: number };
  scoped._authGeneration ??= sessionGeneration();
  if (!sessionIsCurrent(scoped._authGeneration)) throw new Error('session_replaced');
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  const method = (config.method || 'get').toLowerCase();
  if (STATE_CHANGING_METHODS.has(method)) {
    const csrf = getCsrfToken();
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

// Paths where a 401 is "normal" (unauthenticated page visit) — don't kick off
// a refresh loop or redirect away from them.
//
// Wave-67: substring match on the leaf so /vendors/<slug>/login also counts
// as a public path (pre-fix only the slug-less /vendors/login matched and
// the interceptor would redirect a vendor sitting on /vendors/<slug>/login
// back to /vendors/login — losing the marketplace context).
const isOnPublicPath = () => isPublicAuthPath(window.location.pathname);

// The refresh endpoint itself — if THIS 401s, don't recursively try to refresh.
const REFRESH_PATH = '/vendor/auth/refresh';

apiClient.interceptors.response.use(
  // VENDORIQ-PORTAL-5: a status-0 "success" is a network error (see statusZero.ts).
  (response) => {
    const owner = (response.config as InternalAxiosRequestConfig & { _authGeneration?: number })._authGeneration;
    if (owner !== undefined && !sessionIsCurrent(owner)) return Promise.reject(new Error('session_replaced'));
    return rejectStatusZero(response);
  },
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest || !sessionIsCurrent(originalRequest._authGeneration ?? sessionGeneration())) {
      return Promise.reject(error);
    }
    const owner = originalRequest._authGeneration ?? sessionGeneration();
    const reqUrl: string = originalRequest?.url || '';
    // Wave 10 (Frontend audit M2): exact-suffix match instead of
    // substring. `.includes()` would also match a hypothetical
    // `/vendor/auth/refresh-token-info` endpoint, which would
    // disable the auto-refresh + retry on real 401 responses for
    // that endpoint. `.endsWith` only matches the actual /refresh.
    const isRefreshCall = reqUrl.endsWith(REFRESH_PATH);
    const isAuthTransition = /\/vendor\/auth\/(?:login|register|logout)$/.test(reqUrl);

    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshCall && !isAuthTransition &&
        !isSsoRedemptionRequest(reqUrl)) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await serializeSessionRequest(owner, () => axios.post(
          `${apiClient.defaults.baseURL}${REFRESH_PATH}`,
          {},
          { withCredentials: true }
        ));
        if (!sessionIsCurrent(owner)) throw new Error('session_replaced');
        const newToken = data.data.access_token;
        setAccessToken(newToken);
        if (!sessionIsCurrent(owner)) throw new Error('session_replaced');
        setNativeSessionScope(data.data.native_session_scope ?? null);
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        if (!sessionIsCurrent(owner)) return Promise.reject(refreshError);
        setAccessToken(null);
        setNativeSessionScope(null);
        // Only hard-redirect if we're NOT already on a public page.
        // Otherwise the login page itself would redirect to itself on mount.
        //
        // Wave-67: preserve the marketplace slug so a session expiry
        // mid-session lands the vendor on /vendors/<slug>/login, not the
        // slug-less generic /vendors/login. The slug source priority is:
        //   URL > localStorage > empty (fall back to bare /login).
        if (!isOnPublicPath()) {
          // shopSlug is already part of the portal shell's static graph. A
          // static import here avoids a misleading split that Rollup cannot
          // honor because routing imports the same utility.
          const target = authPath('login');
          // SPA basename is /vendors, so prepend it for the hard navigation.
          window.location.href = `/vendors${target}`;
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
